/**
 * Memorial Photo Link builder — client-side draft state (Preview Mode).
 * Live QR/NFC activation remains gated on published memorials.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MEMORIAL_KEEPSAKE_MAIN_PRODUCTS,
  MEMORIAL_KEEPSAKE_PRODUCTS,
  MEMORIAL_KEEPSAKE_SQUARE_GIFT,
} from '../content/memorialKeepsakesContent';
import { DEFAULT_IMAGE_EDITS, normalizeImageEdits, rotateImageEdits } from './memorialPhotoEdits';
import { resolveMemorialPortraitUrl } from './memorial';
import { resolveAssetUrl } from './resolveAssetUrl';
import { refreshKeepsakeImagePreview, uploadKeepsakeImage } from './memorialKeepsakeImageUpload';

export const KEEPSAKE_ORIENTATIONS = {
  portrait: 'portrait',
  landscape: 'landscape',
};

export const KEEPSAKE_FRAME_STYLES = [
  {
    id: 'black',
    label: 'Black frame',
    ring: 'border-[#1c1917] bg-[#1c1917]',
    mat: 'bg-[#faf8f5]',
  },
  {
    id: 'white',
    label: 'White frame',
    ring: 'border-[#e8e2d8] bg-white shadow-sm',
    mat: 'bg-white',
  },
];

export const KEEPSAKE_LAYOUTS = [
  { id: 'portrait_only', label: 'Portrait only', showNameDates: false, showTributeLine: false, showQrNote: false },
  { id: 'portrait_name_dates', label: 'Portrait + name/dates', showNameDates: true, showTributeLine: false, showQrNote: false },
  { id: 'portrait_qr_back', label: 'Portrait + QR on back', showNameDates: true, showTributeLine: false, showQrNote: true },
  {
    id: 'tribute_print',
    label: 'Tribute print with name, dates and short line',
    showNameDates: true,
    showTributeLine: true,
    showQrNote: false,
  },
];

export const KEEPSAKE_IMAGE_SOURCES = {
  portrait: 'portrait',
  upload: 'upload',
  later: 'later',
};

export const DEFAULT_IMAGE_POSITION = {
  zoom: 1,
  x: 50,
  y: 50,
  fitMode: 'cover',
};

export { DEFAULT_IMAGE_EDITS, normalizeImageEdits } from './memorialPhotoEdits';

const DEFAULT_PRODUCT_ID = 'a5_memorial_portrait';

const LEGACY_PRODUCT_MAP = {
  memory_frame_6x6: 'square_gift_preview',
  a4_legacy_tribute: 'a4_memorial_portrait',
  classic_black: 'black',
  warm_oak: 'black',
  white_gallery: 'white',
  soft_gold: 'white',
};

export function detectOrientationFromDimensions(width, height) {
  if (!width || !height) return KEEPSAKE_ORIENTATIONS.portrait;
  return width > height ? KEEPSAKE_ORIENTATIONS.landscape : KEEPSAKE_ORIENTATIONS.portrait;
}

export function loadImageDimensions(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = url;
  });
}

export function getProductFrameClass(product, orientation = KEEPSAKE_ORIENTATIONS.portrait) {
  if (!product) return 'aspect-[148/210]';
  if (orientation === KEEPSAKE_ORIENTATIONS.landscape) {
    return product.frameClassLandscape || product.frameClass || 'aspect-[210/148]';
  }
  return product.frameClassPortrait || product.frameClass || 'aspect-[148/210]';
}

export function normalizeProductId(productId) {
  return LEGACY_PRODUCT_MAP[productId] || productId;
}

export function normalizeFrameStyleId(frameStyleId) {
  return LEGACY_PRODUCT_MAP[frameStyleId] || frameStyleId;
}

export function defaultBuilderState(portraitUrl = null) {
  return {
    productId: DEFAULT_PRODUCT_ID,
    frameStyleId: KEEPSAKE_FRAME_STYLES[0].id,
    layoutId: KEEPSAKE_LAYOUTS[1].id,
    orientation: KEEPSAKE_ORIENTATIONS.portrait,
    orientationManual: false,
    imageSource: portraitUrl ? KEEPSAKE_IMAGE_SOURCES.portrait : KEEPSAKE_IMAGE_SOURCES.later,
    customImageUrl: null,
    uploadedKeepsake: null,
    imagePosition: { ...DEFAULT_IMAGE_POSITION },
    imageEdits: { ...DEFAULT_IMAGE_EDITS },
    step: 1,
  };
}

function storageKey(memorialId, suffix) {
  return `mpl-${suffix}-${memorialId || 'draft'}`;
}

export function loadBuilderState(memorialId) {
  if (!memorialId || typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(storageKey(memorialId, 'builder'));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.productId) parsed.productId = normalizeProductId(parsed.productId);
    if (parsed.frameStyleId) parsed.frameStyleId = normalizeFrameStyleId(parsed.frameStyleId);
    return parsed;
  } catch {
    return null;
  }
}

export function saveBuilderState(memorialId, state) {
  if (!memorialId || typeof window === 'undefined') return;
  try {
    const { customImageUrl, ...persistable } = state;
    const toSave = {
      ...persistable,
      customImageUrl: customImageUrl?.startsWith('blob:') ? null : customImageUrl,
    };
    sessionStorage.setItem(storageKey(memorialId, 'builder'), JSON.stringify(toSave));
  } catch {
    /* ignore quota */
  }
}

export function saveInterestRequest(memorialId, request) {
  if (!memorialId || typeof window === 'undefined') return;
  try {
    const key = storageKey(memorialId, 'requests');
    const prev = JSON.parse(sessionStorage.getItem(key) || '[]');
    prev.push({ ...request, savedAt: new Date().toISOString() });
    sessionStorage.setItem(key, JSON.stringify(prev));
  } catch {
    /* ignore */
  }
}

export function getProductById(productId) {
  const id = normalizeProductId(productId);
  return MEMORIAL_KEEPSAKE_PRODUCTS.find((p) => p.id === id) || MEMORIAL_KEEPSAKE_MAIN_PRODUCTS[1];
}

export function getFrameStyleById(frameStyleId) {
  const id = normalizeFrameStyleId(frameStyleId);
  return KEEPSAKE_FRAME_STYLES.find((f) => f.id === id) || KEEPSAKE_FRAME_STYLES[0];
}

export function getLayoutById(layoutId) {
  return KEEPSAKE_LAYOUTS.find((l) => l.id === layoutId) || KEEPSAKE_LAYOUTS[1];
}

export function resolveBuilderImageUrl(builder, personPortraitUrl, backendBase) {
  if (builder.imageSource === KEEPSAKE_IMAGE_SOURCES.upload) {
    const persisted = builder.uploadedKeepsake?.keepsake_image_url || builder.uploadedKeepsake?.uploaded_image_url;
    if (persisted) return resolveAssetUrl(backendBase, persisted);
    if (builder.customImageUrl) return builder.customImageUrl;
  }
  if (builder.imageSource === KEEPSAKE_IMAGE_SOURCES.portrait && personPortraitUrl) {
    return personPortraitUrl;
  }
  return null;
}

export function getImageStatusLabel(builder, personPortraitUrl, uploadingKeepsake = false) {
  if (uploadingKeepsake) return 'Uploading keepsake image…';
  if (builder.imageSource === KEEPSAKE_IMAGE_SOURCES.upload) {
    if (builder.uploadedKeepsake?.uploaded_image_key || builder.uploadedKeepsake?.uploaded_image_url) {
      return 'Keepsake image uploaded';
    }
    if (builder.customImageUrl?.startsWith('blob:')) return 'Preview only — upload required before saving';
    return 'Custom image selected';
  }
  if (builder.imageSource === KEEPSAKE_IMAGE_SOURCES.portrait && personPortraitUrl) return 'Using memorial portrait';
  if (builder.imageSource === KEEPSAKE_IMAGE_SOURCES.later) return 'Choose image later';
  return 'No image selected yet';
}

export function getOrientationLabel(orientation) {
  return orientation === KEEPSAKE_ORIENTATIONS.landscape ? 'Landscape' : 'Portrait';
}

async function applyAutoOrientationFromUrl(url, setBuilder) {
  try {
    const { width, height } = await loadImageDimensions(url);
    const suggested = detectOrientationFromDimensions(width, height);
    setBuilder((prev) => {
      if (prev.orientationManual) return prev;
      return { ...prev, orientation: suggested };
    });
  } catch {
    /* ignore */
  }
}

export function useMemorialPhotoLinkBuilder(memorial, person, backendBase, { canUploadKeepsake = false } = {}) {
  const memorialId = memorial?.id;
  const personPortraitUrl = useMemo(
    () => (person || memorial ? resolveMemorialPortraitUrl(memorial, person, backendBase, resolveAssetUrl) : null),
    [memorial, person, backendBase],
  );

  const [uploadingKeepsake, setUploadingKeepsake] = useState(false);
  const [builder, setBuilder] = useState(() => {
    const saved = loadBuilderState(memorialId);
    const base = defaultBuilderState(personPortraitUrl);
    return {
      ...base,
      ...(saved || {}),
      imagePosition: { ...DEFAULT_IMAGE_POSITION, ...(saved?.imagePosition || {}) },
      imageEdits: normalizeImageEdits(saved?.imageEdits),
    };
  });

  useEffect(() => {
    if (!memorialId) return;
    saveBuilderState(memorialId, builder);
  }, [memorialId, builder]);

  useEffect(() => {
    if (!memorialId) return;
    const saved = loadBuilderState(memorialId);
    if (saved) {
      setBuilder((prev) => ({
        ...prev,
        ...saved,
        imagePosition: { ...DEFAULT_IMAGE_POSITION, ...(saved.imagePosition || {}) },
        imageEdits: normalizeImageEdits(saved.imageEdits),
      }));
    }
  }, [memorialId]);

  useEffect(() => {
    if (!memorialId || !canUploadKeepsake) return;
    const saved = loadBuilderState(memorialId);
    const ks = saved?.uploadedKeepsake;
    if (!ks || saved?.imageSource !== KEEPSAKE_IMAGE_SOURCES.upload) return;
    refreshKeepsakeImagePreview(memorialId, {
      uploadedImageKey: ks.uploaded_image_key,
      uploadedImageUrl: ks.uploaded_image_url,
    })
      .then((data) => {
        setBuilder((p) => ({
          ...p,
          imageSource: KEEPSAKE_IMAGE_SOURCES.upload,
          uploadedKeepsake: { ...ks, keepsake_image_url: data.keepsake_image_url },
          customImageUrl: resolveAssetUrl(backendBase, data.keepsake_image_url),
        }));
      })
      .catch(() => {
        /* expired or missing — user can re-upload */
      });
  }, [memorialId, canUploadKeepsake, backendBase]);

  const setProductId = useCallback((productId) => setBuilder((p) => ({ ...p, productId: normalizeProductId(productId) })), []);
  const setFrameStyleId = useCallback((frameStyleId) => setBuilder((p) => ({ ...p, frameStyleId: normalizeFrameStyleId(frameStyleId) })), []);
  const setLayoutId = useCallback((layoutId) => setBuilder((p) => ({ ...p, layoutId })), []);
  const setStep = useCallback((step) => setBuilder((p) => ({ ...p, step })), []);

  const setOrientation = useCallback((orientation, manual = true) => {
    setBuilder((p) => ({
      ...p,
      orientation,
      orientationManual: manual ? true : p.orientationManual,
    }));
  }, []);

  const setImagePosition = useCallback((imagePosition) => {
    setBuilder((p) => ({ ...p, imagePosition: { ...DEFAULT_IMAGE_POSITION, ...p.imagePosition, ...imagePosition } }));
  }, []);

  const resetImagePosition = useCallback(() => {
    setBuilder((p) => ({ ...p, imagePosition: { ...DEFAULT_IMAGE_POSITION } }));
  }, []);

  const resetImageEdits = useCallback(() => {
    setBuilder((p) => ({ ...p, imageEdits: { ...DEFAULT_IMAGE_EDITS } }));
  }, []);

  const setImageEdits = useCallback((patch) => {
    setBuilder((p) => ({
      ...p,
      imageEdits: normalizeImageEdits({ ...p.imageEdits, ...patch }),
    }));
  }, []);

  const rotateImageEdit = useCallback((direction) => {
    setBuilder((p) => ({
      ...p,
      imageEdits: rotateImageEdits(p.imageEdits, direction),
    }));
  }, []);

  const fitImage = useCallback(() => {
    setBuilder((p) => ({
      ...p,
      imagePosition: { zoom: 1, x: 50, y: 50, fitMode: 'contain' },
    }));
  }, []);

  const fillImage = useCallback(() => {
    setBuilder((p) => ({
      ...p,
      imagePosition: { zoom: 1, x: 50, y: 50, fitMode: 'cover' },
    }));
  }, []);

  const usePortraitImage = useCallback(() => {
    setBuilder((p) => {
      if (p.customImageUrl?.startsWith('blob:')) URL.revokeObjectURL(p.customImageUrl);
      return {
        ...p,
        imageSource: KEEPSAKE_IMAGE_SOURCES.portrait,
        customImageUrl: null,
        uploadedKeepsake: null,
        orientationManual: false,
      };
    });
    if (personPortraitUrl) applyAutoOrientationFromUrl(personPortraitUrl, setBuilder);
  }, [personPortraitUrl]);

  const chooseImageLater = useCallback(() => {
    setBuilder((p) => {
      if (p.customImageUrl?.startsWith('blob:')) URL.revokeObjectURL(p.customImageUrl);
      return {
        ...p,
        imageSource: KEEPSAKE_IMAGE_SOURCES.later,
        customImageUrl: null,
        uploadedKeepsake: null,
      };
    });
  }, []);

  const setUploadedFile = useCallback(
    async (file) => {
      if (!file || !memorialId) return;
      if (!canUploadKeepsake) {
        const objectUrl = URL.createObjectURL(file);
        setBuilder((p) => {
          if (p.customImageUrl?.startsWith('blob:')) URL.revokeObjectURL(p.customImageUrl);
          return {
            ...p,
            imageSource: KEEPSAKE_IMAGE_SOURCES.upload,
            customImageUrl: objectUrl,
            uploadedKeepsake: null,
            orientationManual: false,
          };
        });
        applyAutoOrientationFromUrl(objectUrl, setBuilder);
        return;
      }
      if (uploadingKeepsake) return;
      setUploadingKeepsake(true);
      try {
        const result = await uploadKeepsakeImage(memorialId, file);
        const previewUrl = resolveAssetUrl(backendBase, result.keepsake_image_url);
        setBuilder((p) => {
          if (p.customImageUrl?.startsWith('blob:')) URL.revokeObjectURL(p.customImageUrl);
          return {
            ...p,
            imageSource: KEEPSAKE_IMAGE_SOURCES.upload,
            customImageUrl: previewUrl,
            uploadedKeepsake: {
              uploaded_image_key: result.uploaded_image_key,
              uploaded_image_url: result.uploaded_image_url,
              keepsake_image_url: result.keepsake_image_url,
              original_filename: result.original_filename,
              mime_type: result.mime_type,
              image_width: result.image_width,
              image_height: result.image_height,
              uploaded_at: result.uploaded_at,
            },
            orientationManual: false,
          };
        });
        if (previewUrl) applyAutoOrientationFromUrl(previewUrl, setBuilder);
        return result;
      } finally {
        setUploadingKeepsake(false);
      }
    },
    [memorialId, canUploadKeepsake, backendBase, uploadingKeepsake],
  );

  const previewImageUrl = resolveBuilderImageUrl(builder, personPortraitUrl, backendBase);
  const product = getProductById(builder.productId);
  const frameStyle = getFrameStyleById(builder.frameStyleId);
  const layout = getLayoutById(builder.layoutId);
  const imageStatus = getImageStatusLabel(builder, personPortraitUrl, uploadingKeepsake);
  const orientation = builder.orientation || KEEPSAKE_ORIENTATIONS.portrait;
  const imagePosition = builder.imagePosition || DEFAULT_IMAGE_POSITION;
  const imageEdits = normalizeImageEdits(builder.imageEdits);

  return {
    builder,
    product,
    frameStyle,
    layout,
    previewImageUrl,
    personPortraitUrl,
    imageStatus,
    orientation,
    imagePosition,
    imageEdits,
    setProductId,
    setFrameStyleId,
    setLayoutId,
    setStep,
    setOrientation,
    setImagePosition,
    resetImagePosition,
    setImageEdits,
    resetImageEdits,
    rotateImageEdit,
    fitImage,
    fillImage,
    usePortraitImage,
    chooseImageLater,
    setUploadedFile,
    uploadingKeepsake,
    mainProducts: MEMORIAL_KEEPSAKE_MAIN_PRODUCTS,
    squareGift: MEMORIAL_KEEPSAKE_SQUARE_GIFT,
  };
}
